import React from 'react';
import { sentimentToPercent, sentimentToPercentDecimal } from '../../utils/formatters';

const getCategory = (pct) => {
  if (pct >= 75) return { label: 'Very Positive', css: 'text-green-600' };
  if (pct >= 55) return { label: 'Positive', css: 'text-green-500' };
  if (pct >= 45) return { label: 'Neutral', css: 'text-yellow-600' };
  if (pct >= 25) return { label: 'Negative', css: 'text-red-500' };
  return { label: 'Very Negative', css: 'text-red-600' };
};

const ScoreGauge = ({ score = 0, label = 'Average Sentiment' }) => {
  const pct = sentimentToPercent(score);
  const category = getCategory(pct);

  // Map -1..+1 to 0..180 degrees
  const angle = ((score + 1) / 2) * 180;
  const clampedAngle = Math.max(0, Math.min(180, angle));

  // SVG arc calculation
  const radius = 70;
  const cx = 100;
  const cy = 90;

  // Needle endpoint
  const needleRad = (Math.PI * (180 - clampedAngle)) / 180;
  const nx = cx + radius * Math.cos(needleRad);
  const ny = cy - radius * Math.sin(needleRad);

  // Color based on percentage
  const color = pct >= 65 ? '#22c55e' : pct >= 35 ? '#eab308' : '#ef4444';

  return (
    <div className="card p-5 text-center h-full flex flex-col justify-center">
      <h3 className="text-sm font-semibold text-gray-700 mb-1">{label}</h3>
      <svg viewBox="0 0 200 125" className="w-full max-w-[200px] mx-auto" role="img" aria-labelledby="gauge-title gauge-desc">
        <title id="gauge-title">{label}</title>
        <desc id="gauge-desc">Sentiment score: {pct} out of 100, {category.label}</desc>
        {/* Background arc */}
        <path
          d="M 30 90 A 70 70 0 0 1 170 90"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="12"
          strokeLinecap="round"
        />
        {/* Colored arc segments (4 equal 45° arcs on the semicircle) */}
        <path d="M 30 90 A 70 70 0 0 1 50.5 40.5" fill="none" stroke="#ef4444" strokeWidth="12" strokeLinecap="round" />
        <path d="M 50.5 40.5 A 70 70 0 0 1 100 20" fill="none" stroke="#f97316" strokeWidth="12" />
        <path d="M 100 20 A 70 70 0 0 1 149.5 40.5" fill="none" stroke="#eab308" strokeWidth="12" />
        <path d="M 149.5 40.5 A 70 70 0 0 1 170 90" fill="none" stroke="#22c55e" strokeWidth="12" strokeLinecap="round" />
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={color} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="4" fill={color} />
        {/* Score (0-100) below needle */}
        <text x={cx} y={cy + 22} textAnchor="middle" fill={color} fontSize="18" fontWeight="bold">
          {sentimentToPercentDecimal(score)}
        </text>
      </svg>
      <div className="flex justify-between text-xs text-gray-400 px-2 -mt-1">
        <span>Negative</span>
        <span>Neutral</span>
        <span>Positive</span>
      </div>
      <p className={`text-sm font-semibold mt-1.5 ${category.css}`}>{category.label}</p>
      <p className="text-xs text-gray-400">{sentimentToPercentDecimal(score)}/100</p>
    </div>
  );
};

export default React.memo(ScoreGauge);
